import { useCallback, useEffect, useRef, useState } from "react";
import { localStore } from "./localStore";

/** OSCE-style device-first persistence, scoped to a single CBT sitting.
 * Saves every choice immediately to localStorage, sends latest choices to
 * MongoDB in order, retries outages, and exposes an awaitable final flush.
 */
export function useCbtAnswerQueue(key, send, onRejected) {
  const itemsRef = useRef([]);
  const [pending, setPending] = useState(0);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const taskRef = useRef(null);
  const activeKeyRef = useRef(key);
  const timer = useRef(null);
  const sendRef = useRef(send);
  const rejectRef = useRef(onRejected);
  const lastRejectedRef = useRef("");
  sendRef.current = send;
  rejectRef.current = onRejected;
  activeKeyRef.current = key;

  const persist = useCallback(items => {
    // An earlier sitting's in-flight request must never remove answers from
    // the queue belonging to a later sitting.
    if (activeKeyRef.current !== key) return;
    itemsRef.current = items;
    setPending(items.length);
    if (key) {
      try {
        window.localStorage.setItem(key, JSON.stringify(items));
      } catch {
        rejectRef.current?.(new Error("Cannot save answers on this device. Check storage settings and contact the invigilator."));
      }
    }
  }, [key]);

  const flush = useCallback(() => {
    if (activeKeyRef.current !== key) return Promise.resolve(false);
    if (taskRef.current?.key === key) return taskRef.current.promise;
    if (!key || !itemsRef.current.length) return Promise.resolve(true);
    // Capture this sitting's sender. A later React render may switch to the
    // next subject sitting while an old HTTP request is still in flight.
    const sender = sendRef.current;
    const job = (async () => {
      setSyncing(true);
      let clean = true;
      try {
        while (activeKeyRef.current === key && itemsRef.current.length) {
          const batch = itemsRef.current.slice(0, 200);
          try {
            const latest = new Map();
            for (const row of batch) latest.set(row.questionId, row);
            await sender([...latest.values()]);
            if (activeKeyRef.current !== key) return false;
            lastRejectedRef.current = "";
            const processed = new Set(batch.map(row => row.localId));
            persist(itemsRef.current.filter(row => !processed.has(row.localId)));
            setOnline(true);
          } catch (err) {
            if (activeKeyRef.current !== key) return false;
            clean = false;
            if (err.status === 0 || (err.status || 0) >= 500) {
              if (err.status === 0) setOnline(false);
              break;
            }
            const failure = `${key}:${batch[0]?.localId}:${batch.length}:${err.status}:${err.message}`;
            if (lastRejectedRef.current !== failure) {
              lastRejectedRef.current = failure;
              rejectRef.current?.(err);
            }
            break;
          }
        }
      } finally {
        if (activeKeyRef.current === key) setSyncing(false);
      }
      return activeKeyRef.current === key && clean && itemsRef.current.length === 0;
    })();
    taskRef.current = { key, promise: job };
    job.finally(() => { if (taskRef.current?.promise === job) taskRef.current = null; });
    return job;
  }, [key, persist]);

  const enqueue = useCallback(item => {
    persist([...itemsRef.current, { ...item, localId: `${Date.now()}-${Math.random()}` }]);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { timer.current = null; void flush(); }, 350);
  }, [flush, persist]);

  useEffect(() => {
    lastRejectedRef.current = "";
    const loaded = key ? localStore.get(key, []) : [];
    itemsRef.current = Array.isArray(loaded) ? loaded : [];
    setPending(itemsRef.current.length);
    setSyncing(false);
    if (key && itemsRef.current.length) void flush();
    const reconnect = () => { setOnline(true); void flush(); };
    const disconnected = () => setOnline(false);
    window.addEventListener("online", reconnect);
    window.addEventListener("offline", disconnected);
    const interval = setInterval(() => { if (navigator.onLine && itemsRef.current.length) void flush(); }, 4000);
    return () => {
      window.removeEventListener("online", reconnect);
      window.removeEventListener("offline", disconnected);
      clearInterval(interval);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [key, flush]);
  return { enqueue, flush, pending, pendingItems: itemsRef.current, online, syncing };
}
